import { useMemo } from 'react';
import { css } from '@emotion/react';
import { PartnerInfo } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import dayjs from 'dayjs';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { PartnerInformationFields, UserRole } from '../../utils/constants';
import { checkRbacAccess, formatString } from '../../utils/helpers';
import { IntegrationsMetadata } from '../../utils/typings';

interface PartnerInformationTabProps {
  partnerInformation?: PartnerInfo;
  integrationsMetadata?: IntegrationsMetadata;
  userRole?: keyof typeof UserRole;
}

const PartnerInformationTab = ({ partnerInformation, integrationsMetadata, userRole }: PartnerInformationTabProps) => {
  const hasViewAccess = useMemo(
    () => checkRbacAccess(integrationsMetadata?.partnerInformation, userRole).hasViewAccess,
    [integrationsMetadata, userRole]
  );

  const infoFields = useMemo(() => {
    const authorizedSince = partnerInformation?.authorizedIntegrationSince
      ? dayjs(partnerInformation.authorizedIntegrationSince).format('LLL')
      : undefined;

    return [
      [PartnerInformationFields.PARENT_COMPANY, partnerInformation?.parentCompany],
      [PartnerInformationFields.PARTNER_STATUS, partnerInformation?.partnerStatus],
      [PartnerInformationFields.PARTNER_INTEGRATION_STATUS, partnerInformation?.partnerIntegrationStatus],
      [PartnerInformationFields.AUTHORIZED_INTEGRATION_SINCE, authorizedSince],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');
  }, [partnerInformation]);

  return (
    <>
      {!hasViewAccess ? (
        <Text>No Partner Information available for your role.</Text>
      ) : (
        <div css={partnerInfoContainerStyle}>
          <div css={headerStyle}>
            <Button aria-label='Edit' css={iconButtonStyle}>
              <Icon name='edit' size={20} />
            </Button>
          </div>

          <div css={partnerGridStyle}>
            {infoFields.length > 0 ? (
              infoFields.map(([label, value]) => (
                <div key={label}>
                  <Text weight='bold'>{label}</Text>
                  <Text>{formatString(value ?? '') || '—'}</Text>
                </div>
              ))
            ) : (
              <Text>No partner metadata available.</Text>
            )}
          </div>

          {partnerInformation?.details && (
            <>
              <Text weight='bold'>{PartnerInformationFields.PARTNER_DETAILS}</Text>
              <Text>{partnerInformation.details}</Text>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default PartnerInformationTab;

const partnerInfoContainerStyle = css`
  margin-top: ${theme.spacing(3)};
  border: 1px solid ${theme.colors.neutral[20]};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(3)};
  position: relative;
`;

const headerStyle = css`
  position: absolute;
  top: ${theme.spacing(2)};
  right: ${theme.spacing(2)};
  z-index: 1;
`;

const iconButtonStyle = css`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing(0.5)};
  border-radius: ${theme.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.black};

  &:hover,
  &:active:not([aria-disabled='true']) {
    background-color: ${theme.colors.neutral[10]};

    & svg {
      color: ${theme.colors.black};
    }
  }
`;

const partnerGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  column-gap: ${theme.spacing(4)};
  row-gap: ${theme.spacing(3)};
  align-items: start;
  margin-bottom: ${theme.spacing(4)};
  padding-top: ${theme.spacing(1)};
`;
